import dynamic from 'next/dynamic';
import React, { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { WWWCompanyDocument } from '../../common/documents/WWWCompanyDocument';
import { CompanyActions, useCompanyActionsInSearch, useCompanyActionsInPage } from './useCompanyActions';

interface CompanyActionMenuOptions {
  hideLikeDislike?: boolean;
}

/**
 * An easy way to render company menu and recieve handler functions for it
 */
const useCompanyActionMenu = (
  companyID: number,
  companyActions: CompanyActions,
  { hideLikeDislike = false }: CompanyActionMenuOptions = {}
) => {
  const [initial, setInitial] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement>(null);

  const { isLiked, isDisliked, listIDs } = companyActions;

  const handleMenuClose = useCallback(() => setAnchorEl(null), []);

  const handleMenuOpen = useCallback((event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget), []);

  useEffect(() => setInitial((state) => state || !!anchorEl), [anchorEl]);

  const isVisible = initial || !!anchorEl;

  // Import menu only if required
  const CompanyActionMenu = useMemo(
    () =>
      isVisible
        ? dynamic(() => import('./CompanyActionMenu').then(({ CompanyActionMenu }) => CompanyActionMenu), { loading: () => null })
        : null,
    [isVisible]
  );
  const renderMenu = useCallback(() => {
    if (!CompanyActionMenu) return null;

    return (
      <CompanyActionMenu
        companyID={companyID}
        anchorEl={anchorEl}
        hideLikeDislike={hideLikeDislike}
        onClose={handleMenuClose}
        {...companyActions}
      />
    );
  }, [companyID, isLiked, isDisliked, listIDs, anchorEl, CompanyActionMenu]);

  return {
    handleMenuOpen,
    handleMenuClose,
    renderMenu,
  };
};

/**
 * Handle company menu in search page.
 * Updates the search result list and cache accordingly.
 */
export const useCompanyActionMenuInSearch = () => {
  const [menuState, setMenuState] = useState<{ companyID: number | null; currentTarget: HTMLElement | null }>({
    companyID: null,
    currentTarget: null,
  });

  const { companyID, currentTarget } = menuState;

  const companyActions = useCompanyActionsInSearch(companyID);
  const { handleMenuOpen: _handleMenuOpen, ...companyActionMenu } = useCompanyActionMenu(companyID, companyActions);

  // We have to let companyID state update before opening the dialog, this does just that
  useEffect(() => {
    if (currentTarget) {
      setMenuState({ ...menuState, currentTarget: null });
      _handleMenuOpen({ currentTarget } as any);
    }
  }, [currentTarget]);

  const handleMenuOpen = useCallback(
    (companyID: number, event: MouseEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();

      setMenuState({ companyID, currentTarget: event.currentTarget });
    },
    [_handleMenuOpen]
  );

  return { ...companyActionMenu, handleMenuOpen };
};

/**
 * Handle company menu in company page.
 * Updates the company data accordingly.
 */
export const useCompanyActionMenuInPage = (company: WWWCompanyDocument) => {
  const companyActions = useCompanyActionsInPage(company);
  return useCompanyActionMenu(company.companyID, companyActions, { hideLikeDislike: true });
};
